import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { X, Minus, Plus, Trash2, Loader2, Tag, Ticket, Check } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { SHOP } from "@/constants/testIds";
import { toast } from "sonner";
import { track } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CartDrawer() {
  const { items, setQty, remove, total, count, open, setOpen, clear } = useCart();
  const [showCheckout, setShowCheckout] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [acceptAge, setAcceptAge] = useState(false);
  const [loading, setLoading] = useState(false);
  const [discountPct, setDiscountPct] = useState(0);
  // Applied prepaid codes: [{code, balance_eur, applied_eur}]
  const [prepaid, setPrepaid] = useState([]);
  const [prepaidInput, setPrepaidInput] = useState("");
  const [prepaidLoading, setPrepaidLoading] = useState(false);

  const hasAlcohol = items.some((it) => it.is_alcohol);

  // Sorted view: regular items first, ecocup deposit last. Used in 2 places.
  const sortedItems = useMemo(
    () => [...items].sort((a, b) => Number(!!a.is_deposit) - Number(!!b.is_deposit)),
    [items]
  );

  useEffect(() => {
    axios.get(`${API}/config/sales`).then((r) => setDiscountPct(r.data.discount_pct || 0)).catch(() => {});
  }, []);

  // Eligible total for discount (everything except ecocup caution)
  const eligibleTotal = items
    .filter((it) => !it.is_deposit)
    .reduce((s, it) => s + it.price_eur * it.quantity, 0);
  const cautionTotal = items
    .filter((it) => it.is_deposit)
    .reduce((s, it) => s + it.price_eur * it.quantity, 0);
  const discountAmount = Math.round(eligibleTotal * discountPct) / 100;
  const subtotalAfterDiscount = Math.round((total - discountAmount) * 100) / 100;

  // Apply prepaid codes greedily to subtotalAfterDiscount
  const prepaidApplied = prepaid.reduce(
    (acc, p) => {
      const applied = Math.min(p.balance_eur, Math.max(0, acc.remaining));
      const appliedRounded = Math.round(applied * 100) / 100;
      return {
        remaining: Math.max(0, Math.round((acc.remaining - applied) * 100) / 100),
        lines: [...acc.lines, { ...p, applied_eur: appliedRounded }],
      };
    },
    { remaining: subtotalAfterDiscount, lines: [] }
  ).lines;
  const prepaidUsedEur = prepaidApplied.reduce((s, p) => s + p.applied_eur, 0);
  const amountDue = Math.max(0, Math.round((subtotalAfterDiscount - prepaidUsedEur) * 100) / 100);

  const addPrepaidCode = async () => {
    const code = prepaidInput.trim().toUpperCase();
    if (!code) return;
    if (prepaid.find((p) => p.code === code)) {
      toast.error("Ce code est déjà ajouté");
      return;
    }
    setPrepaidLoading(true);
    try {
      const { data } = await axios.post(`${API}/prepaid/check`, { code });
      if (data.balance_eur <= 0) {
        toast.error("Ce code est épuisé");
        return;
      }
      setPrepaid((p) => [...p, { code: data.code, balance_eur: data.balance_eur }]);
      setPrepaidInput("");
      toast.success(`Code +${data.balance_eur.toFixed(2)} €`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Code invalide");
    } finally {
      setPrepaidLoading(false);
    }
  };
  const removePrepaid = (code) => setPrepaid((p) => p.filter((x) => x.code !== code));

  const handlePay = async (e) => {
    e.preventDefault();
    if (hasAlcohol && !acceptAge) {
      toast.error("Confirmez que vous avez plus de 18 ans");
      return;
    }
    setLoading(true);
    try {
      const cartItems = items.map((it) => {
        if (it.is_pack && it.components) {
          return {
            product_id: it.product_id,
            quantity: it.quantity,
            components: it.components.map((c) => ({
              product_id: c.product_id,
              quantity: c.quantity,
            })),
          };
        }
        return { product_id: it.product_id, quantity: it.quantity };
      });
      const payload = {
        items: cartItems,
        customer_email: email.trim(),
        customer_name: name.trim(),
        origin_url: window.location.origin,
        prepaid_codes: prepaidApplied.filter((p) => p.applied_eur > 0).map((p) => p.code),
      };
      const { data } = await axios.post(`${API}/checkout/session`, payload);
      track("checkout_started");
      if (data.fully_prepaid) {
        clear();
        sessionStorage.setItem("urbx_pending_session", `prepaid-${data.booking_ref}`);
        window.location.assign(`/success?session_id=prepaid-${data.booking_ref}`);
      } else {
        sessionStorage.setItem("urbx_pending_session", data.session_id);
        window.location.assign(data.url);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message = detail || "Erreur lors de la création du paiement";
      toast.error(message);
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid={SHOP.cartDrawer}>
      {/* Backdrop */}
      <button
        aria-label="Fermer"
        onClick={() => setOpen(false)}
        className="flex-1 bg-[#14230A]/70 backdrop-blur-sm"
      />

      {/* Drawer */}
      <aside className="w-full max-w-[480px] h-full bg-[#FDF0DF] border-l-4 border-[#14230A] flex flex-col">
        <div className="flex items-center justify-between px-6 py-5 border-b-2 border-[#14230A] shrink-0">
          <div>
            <div className="font-cond uppercase text-[11px] tracking-[0.3em] text-[#14230A]/60">
              Panier
            </div>
            <h2 className="font-display text-3xl text-[#14230A]">
              {count} {count > 1 ? "articles" : "article"}
            </h2>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="w-10 h-10 border-2 border-[#14230A] flex items-center justify-center hover:bg-[#14230A] hover:text-[#FDF0DF]"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {!showCheckout ? (
          <>
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <div
                  data-testid={SHOP.cartEmpty}
                  className="text-center py-16 text-[#14230A]/60 font-cond tracking-wider"
                >
                  Ton panier est vide. <br /> Va piocher dans le catalogue.
                </div>
              ) : (
                <ul className="space-y-3">
                  {sortedItems.map((it) => {
                    const isDeposit = it.is_deposit;
                    const isPack = it.is_pack;
                    return (
                      <li
                        key={it.instance_id}
                        data-testid={SHOP.cartItem(it.instance_id)}
                        className={`border-2 p-3 flex items-start gap-3 ${
                          isDeposit
                            ? "border-[#FF4D00] bg-[#FF4D00]/10"
                            : isPack
                            ? "border-[#14230A] bg-[#14230A] text-[#FDF0DF]"
                            : "border-[#14230A]"
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className={`font-display text-lg leading-tight ${isPack ? "" : "text-[#14230A]"}`}>
                              {it.name}
                            </div>
                            {isDeposit && (
                              <span className="px-1.5 py-0.5 bg-[#FF4D00] text-[#FDF0DF] font-cond uppercase text-[9px] tracking-[0.2em]">
                                Caution
                              </span>
                            )}
                            {isPack && (
                              <span className="px-1.5 py-0.5 bg-[#FF4D00] text-[#FDF0DF] font-cond uppercase text-[9px] tracking-[0.2em]">
                                Pack
                              </span>
                            )}
                          </div>
                          <div className={`font-cond text-xs tracking-widest mt-0.5 ${isPack ? "opacity-60" : "text-[#14230A]/60"}`}>
                            {it.price_eur.toFixed(2)} € · {it.unit_label}
                          </div>
                          {isPack && it.components && (
                            <ul className="mt-2 space-y-0.5 text-xs opacity-90">
                              {it.components.map((c) => (
                                <li key={`${c.product_id}-${c.name}`}>
                                  ↳ {c.name} × {c.quantity}
                                </li>
                              ))}
                            </ul>
                          )}
                          {isDeposit && (
                            <div className="mt-1 text-[11px] leading-snug text-[#14230A]/70">
                              Remboursable en espèces au stand quand tu rends ta cup.
                            </div>
                          )}
                        </div>
                        {!isPack && (
                          <div className={`flex items-center border-2 ${isPack ? "border-[#FDF0DF]" : "border-[#14230A]"}`}>
                            <button
                              data-testid={SHOP.cartQtyMinus(it.instance_id)}
                              onClick={() => setQty(it.instance_id, it.quantity - 1)}
                              disabled={it.quantity <= 0}
                              className="w-8 h-9 flex items-center justify-center hover:bg-[#14230A] hover:text-[#FDF0DF] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#14230A]"
                              aria-label="Diminuer"
                            >
                              <Minus className="w-3.5 h-3.5" />
                            </button>
                            <span className="w-8 text-center font-cond font-bold">{it.quantity}</span>
                            <button
                              data-testid={SHOP.cartQtyPlus(it.instance_id)}
                              onClick={() => setQty(it.instance_id, it.quantity + 1)}
                              disabled={it.quantity >= it.stock_remaining}
                              className="w-8 h-9 flex items-center justify-center hover:bg-[#14230A] hover:text-[#FDF0DF] disabled:opacity-30"
                              aria-label="Augmenter"
                            >
                              <Plus className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                        {(!isDeposit || it.quantity > 0) && (
                          <button
                            data-testid={SHOP.cartRemove(it.instance_id)}
                            onClick={() => remove(it.instance_id)}
                            className={`w-9 h-9 border-2 flex items-center justify-center ${
                              isPack
                                ? "border-[#FDF0DF] hover:bg-[#C53A1B] hover:border-[#C53A1B]"
                                : "border-[#14230A] hover:bg-[#C53A1B] hover:border-[#C53A1B] hover:text-[#FDF0DF]"
                            }`}
                            aria-label="Supprimer"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t-2 border-[#14230A] p-6 space-y-3 shrink-0">
                {discountPct > 0 && (
                  <div
                    data-testid="cart-discount-banner"
                    className="flex items-center gap-2 bg-[#FF4D00] text-[#FDF0DF] px-3 py-2 text-xs font-cond uppercase tracking-[0.15em]"
                  >
                    <Tag className="w-3.5 h-3.5" /> Early-bird −{discountPct}% appliqué
                  </div>
                )}
                <div className="flex items-baseline justify-between">
                  <span className="font-cond uppercase tracking-[0.2em] text-sm">Total</span>
                  <span
                    data-testid={SHOP.cartTotal}
                    className="font-display text-4xl text-[#14230A]"
                  >
                    {(discountPct > 0 ? subtotalAfterDiscount : total).toFixed(2)} €
                  </span>
                </div>
                {discountPct > 0 && (
                  <div className="flex items-baseline justify-between text-xs text-[#14230A]/60">
                    <span>Avant remise</span>
                    <span className="line-through">{total.toFixed(2)} €</span>
                  </div>
                )}
                <button
                  data-testid={SHOP.cartCheckoutBtn}
                  onClick={() => setShowCheckout(true)}
                  className="w-full h-12 bg-[#14230A] text-[#FDF0DF] font-cond uppercase tracking-[0.2em] font-bold hover:bg-[#FF4D00] transition-colors btn-brut-orange"
                >
                  Passer commande →
                </button>
                <button
                  data-testid={SHOP.cartContinueBtn}
                  onClick={() => setOpen(false)}
                  className="w-full h-10 border-2 border-[#14230A] font-cond uppercase tracking-[0.2em] text-sm hover:bg-[#14230A] hover:text-[#FDF0DF]"
                >
                  Continuer mes achats
                </button>
              </div>
            )}
          </>
        ) : (
          <form onSubmit={handlePay} className="flex-1 min-h-0 flex flex-col">
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-5">
              <div>
                <div className="font-cond uppercase text-[11px] tracking-[0.3em] text-[#14230A]/60 mb-1">
                  Étape 2
                </div>
                <h3 className="font-display text-2xl text-[#14230A]">Tes infos</h3>
                <p className="text-sm text-[#14230A]/70 mt-1">
                  On envoie le ticket QR sur ton email. C’est lui qu’il faudra présenter au stand.
                </p>
              </div>

              <div>
                <label className="font-cond uppercase text-[11px] tracking-[0.2em] text-[#14230A]">
                  Prénom & Nom
                </label>
                <input
                  data-testid={SHOP.checkoutName}
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1.5 w-full h-12 px-3 bg-transparent border-2 border-[#14230A] focus:border-[#FF4D00] outline-none font-cond text-base"
                  placeholder="Ex: Lina Dubois"
                />
              </div>
              <div>
                <label className="font-cond uppercase text-[11px] tracking-[0.2em] text-[#14230A]">
                  Email
                </label>
                <input
                  data-testid={SHOP.checkoutEmail}
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5 w-full h-12 px-3 bg-transparent border-2 border-[#14230A] focus:border-[#FF4D00] outline-none font-cond text-base"
                  placeholder="ton.email@exemple.fr"
                />
              </div>

              {hasAlcohol && (
                <label className="flex items-start gap-3 cursor-pointer border-2 border-[#14230A] p-3 bg-[#FDF0DF]">
                  <input
                    type="checkbox"
                    checked={acceptAge}
                    onChange={(e) => setAcceptAge(e.target.checked)}
                    className="mt-0.5 w-5 h-5 accent-[#FF4D00]"
                  />
                  <span className="text-sm">
                    Je certifie avoir <b>18 ans ou plus</b>. La vente d’alcool aux mineurs est interdite. L’abus
                    d’alcool est dangereux pour la santé.
                  </span>
                </label>
              )}

              <div className="border-2 border-[#14230A] p-3 space-y-2">
                <div className="font-cond uppercase text-[10px] tracking-[0.3em] text-[#14230A]/70 flex items-center gap-2">
                  <Ticket className="w-3 h-3" /> Codes prépayés
                </div>
                <div className="flex gap-2">
                  <input
                    data-testid="prepaid-input"
                    value={prepaidInput}
                    onChange={(e) => setPrepaidInput(e.target.value.toUpperCase())}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addPrepaidCode();
                      }
                    }}
                    placeholder="Ex: 4619485YOB"
                    className="flex-1 h-10 px-2 bg-transparent border-2 border-[#14230A] font-cond uppercase tracking-widest text-sm"
                  />
                  <button
                    data-testid="prepaid-apply"
                    type="button"
                    onClick={addPrepaidCode}
                    disabled={prepaidLoading || !prepaidInput.trim()}
                    className="h-10 px-3 bg-[#14230A] text-[#FDF0DF] font-cond uppercase tracking-[0.2em] text-xs disabled:opacity-50"
                  >
                    {prepaidLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Ajouter"}
                  </button>
                </div>
                {prepaidApplied.length > 0 && (
                  <ul className="space-y-1 pt-1">
                    {prepaidApplied.map((p) => (
                      <li
                        key={p.code}
                        data-testid={`prepaid-line-${p.code}`}
                        className="flex items-center justify-between text-xs"
                      >
                        <span className="inline-flex items-center gap-1.5 font-cond uppercase tracking-widest">
                          <Check className="w-3 h-3 text-[#FF4D00]" /> {p.code}
                          <span className="opacity-60">
                            (solde {p.balance_eur.toFixed(2)} €)
                          </span>
                        </span>
                        <span className="flex items-center gap-2">
                          <span className="font-bold">−{p.applied_eur.toFixed(2)} €</span>
                          <button
                            type="button"
                            onClick={() => removePrepaid(p.code)}
                            aria-label="Retirer"
                            className="w-5 h-5 border border-[#14230A] inline-flex items-center justify-center hover:bg-[#C53A1B] hover:border-[#C53A1B] hover:text-[#FDF0DF]"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="bg-[#14230A] text-[#FDF0DF] p-4 -mx-2">
                <div className="font-cond text-[10px] tracking-[0.3em] uppercase opacity-70">
                  Récap
                </div>
                <ul className="mt-2 space-y-1 text-sm">
                  {sortedItems.map((it) => (
                    <li key={it.instance_id}>
                      <div className="flex justify-between">
                        <span className="opacity-90">
                          {it.name} × {it.quantity}
                        </span>
                        <span className="font-cond">{(it.price_eur * it.quantity).toFixed(2)} €</span>
                      </div>
                      {it.is_pack && it.components && (
                        <div className="text-[11px] opacity-60 ml-2">
                          ↳ {it.components.map((c) => `${c.name}×${c.quantity}`).join(" · ")}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
                <div className="mt-3 pt-3 border-t border-[#FDF0DF]/30 space-y-1 text-sm">
                  <div className="flex items-baseline justify-between opacity-90">
                    <span>Sous-total</span>
                    <span className="font-cond">{total.toFixed(2)} €</span>
                  </div>
                  {discountPct > 0 && (
                    <div data-testid="checkout-discount-line" className="flex items-baseline justify-between text-[#FF4D00]">
                      <span className="font-cond uppercase tracking-widest text-xs">Early-bird −{discountPct}%</span>
                      <span className="font-cond">−{discountAmount.toFixed(2)} €</span>
                    </div>
                  )}
                  {prepaidUsedEur > 0 && (
                    <div data-testid="checkout-prepaid-line" className="flex items-baseline justify-between text-[#FF4D00]">
                      <span className="font-cond uppercase tracking-widest text-xs">Prépayés</span>
                      <span className="font-cond">−{prepaidUsedEur.toFixed(2)} €</span>
                    </div>
                  )}
                  <div className="flex items-baseline justify-between pt-2">
                    <span className="font-cond uppercase tracking-[0.2em] text-sm">À payer</span>
                    <span data-testid="checkout-amount-due" className="font-display text-3xl">{amountDue.toFixed(2)} €</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t-2 border-[#14230A] p-6 space-y-3">
              <button
                data-testid={SHOP.checkoutPayBtn}
                type="submit"
                disabled={loading}
                className="w-full h-13 py-3 bg-[#FF4D00] text-[#FDF0DF] font-cond uppercase tracking-[0.2em] font-bold flex items-center justify-center gap-2 hover:bg-[#D43E00] disabled:opacity-60"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                {loading
                  ? "Redirection…"
                  : amountDue <= 0
                  ? `Valider · 0,00 € à payer`
                  : `Payer ${amountDue.toFixed(2)} € avec Stripe`}
              </button>
              <button
                type="button"
                onClick={() => setShowCheckout(false)}
                className="w-full h-10 border-2 border-[#14230A] font-cond uppercase tracking-[0.2em] text-sm hover:bg-[#14230A] hover:text-[#FDF0DF]"
              >
                ← Retour au panier
              </button>
            </div>
          </form>
        )}
      </aside>
    </div>
  );
}
